import {
  MINIMUM_VIDEO_COMPARISON_SELECTIONS,
  parseVideoSelectionUrl,
  type ReportFilterStateV1,
} from "@meta-video/contracts";
import { Alert, Button, Drawer, Group, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useId, useRef, useState } from "react";

import { restoreFocus } from "../../app/a11y.js";
import { SessionExpiredError } from "../../shared/api/client.js";
import { parseComparisonViewSearch } from "../../shared/url/comparisonSearch.js";
import {
  parseVideoWorkspaceSearch,
  serializeSavedViewSearch,
} from "../../shared/url/workspaceSearch.js";
import { ReportWorkspace } from "../report/ReportWorkspace.js";
import { CustomerHeaderActionPortal } from "../workspace/CustomerShell.js";
import { ShareLinkButton } from "../workspace/ShareLinkButton.js";
import { ActiveFilterChips, type FilterSection } from "./ActiveFilterChips.js";
import { AdNameSearchProvider } from "./AdNameSearchInput.js";
import { FilterBar } from "./FilterBar.js";
import { usePersistentFilterState } from "./FilterStateProvider.js";
import { SavedViewHeaderActions } from "./SavedViews.js";
import { useFilterState, type FilterStateController } from "./useFilterState.js";

export interface FilterWorkspaceProps {
  readonly onAccessChanged?: () => void;
  readonly onRunnableFilters?: (filters: ReportFilterStateV1) => void;
  readonly onSessionExpired: () => void;
  readonly unknownDurationCount?: number;
}

export function FilterWorkspace({
  onAccessChanged,
  onRunnableFilters,
  onSessionExpired,
  unknownDurationCount,
}: FilterWorkspaceProps) {
  const state = useFilterState({ onRunnable: onRunnableFilters });
  const loadError = state.status === "blocked" ? state.error : undefined;

  useEffect(() => {
    if (loadError instanceof SessionExpiredError) onSessionExpired();
  }, [loadError, onSessionExpired]);

  return (
    <FilterWorkspaceContent
      onAccessChanged={onAccessChanged}
      onSessionExpired={onSessionExpired}
      state={state}
      unknownDurationCount={unknownDurationCount}
    />
  );
}

export function PersistentFilterWorkspace({
  onAccessChanged,
  onSessionExpired,
  unknownDurationCount,
}: Omit<FilterWorkspaceProps, "onRunnableFilters">) {
  const state = usePersistentFilterState();
  return (
    <FilterWorkspaceContent
      onAccessChanged={onAccessChanged}
      onSessionExpired={onSessionExpired}
      state={state}
      unknownDurationCount={unknownDurationCount}
    />
  );
}

function FilterWorkspaceContent({
  onAccessChanged,
  onSessionExpired,
  state,
  unknownDurationCount,
}: {
  readonly onAccessChanged?: () => void;
  readonly onSessionExpired: () => void;
  readonly state: FilterStateController;
  readonly unknownDurationCount?: number;
}) {
  const desktopRail = useMediaQuery("(min-width: 80em)");
  const [filtersOpened, setFiltersOpened] = useState(false);
  const [requestedSection, setRequestedSection] = useState<FilterSection>("brands");
  const [reportUnknownDurationCount, setReportUnknownDurationCount] = useState<number>();
  const drawerId = useId();
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement>(null);
  const filters = state.status === "runnable"
    ? state.filters
    : state.status === "blocked" && state.reason === "empty-brand-scope"
      ? state.draft
      : null;
  const search = window.location.search;
  const videoWorkspace = parseVideoWorkspaceSearch(search);
  const comparisonView = parseComparisonViewSearch(search);
  const comparisonSelection = parseVideoSelectionUrl(search);
  const detailOpened = videoWorkspace.kind === "valid" && videoWorkspace.videoView;
  const comparisonOpened = videoWorkspace.kind === "valid"
    && !videoWorkspace.videoView
    && comparisonView.kind === "valid"
    && comparisonSelection.kind === "valid"
    && comparisonSelection.selections.length >= MINIMUM_VIDEO_COMPARISON_SELECTIONS;
  const immersiveMode = detailOpened || comparisonOpened;
  const closeFilters = () => setFiltersOpened(false);
  const openFilters = (section: FilterSection, trigger: HTMLButtonElement) => {
    returnFocusRef.current = trigger;
    setRequestedSection(section);
    setFiltersOpened(true);
  };
  const filterEditor = filters && state.catalog ? (
    <FilterBar
      catalog={state.catalog}
      filters={filters}
      onChange={state.setFilters}
      subgroupCatalogs={state.subgroupCatalogs}
      unknownDurationCount={state.status === "runnable"
        ? unknownDurationCount ?? reportUnknownDurationCount
        : undefined}
    />
  ) : null;
  const focusRequestedSection = () => {
    const section = document.querySelector<HTMLElement>(
      `[data-filter-section="${requestedSection}"]`,
    );
    const target = section?.querySelector<HTMLElement>("input")
      ?? section?.querySelector<HTMLElement>("button");
    target?.focus();
  };

  useEffect(() => {
    if (!filtersOpened) return;
    focusRequestedSection();
  }, [desktopRail, filtersOpened, requestedSection]);

  if (state.status === "loading") {
    return <p aria-live="polite" role="status">Loading filters and saved views…</p>;
  }

  return (
    <Stack gap="lg" w="100%">
      {!immersiveMode && state.status === "runnable" && state.isShareReady ? (
        <CustomerHeaderActionPortal>
          <Group className="customer-header__saved-view-actions" gap="xs" wrap="nowrap">
            {state.savedViewsStatus === "success" ? (
              <SavedViewHeaderActions
                activeView={state.activeView}
                compactLabel="Save"
                filters={state.filters}
                isDirty={state.isDirty}
                isPartial={state.isPartial}
                onChanged={async (view, expectedWorkspaceKey) => {
                  await state.adoptSavedViewIfCurrent(view, expectedWorkspaceKey);
                  await state.refreshSavedViews();
                }}
                onMetadataChanged={async (view) => {
                  if (view) state.updateSavedViewBaseline(view);
                  await state.refreshSavedViews();
                }}
                onSessionExpired={onSessionExpired}
                workspaceKey={state.workspaceMutationKey}
              />
            ) : null}
            <ShareLinkButton compactLabel="Share" label={state.activeView && state.isDirty
              ? "Share current report"
              : "Share"}
            />
            {state.activeView ? (
              <ShareLinkButton
                compactLabel="Link"
                label="Copy Saved View link"
                url={`/?${serializeSavedViewSearch(state.activeView.id)}`}
              />
            ) : null}
          </Group>
        </CustomerHeaderActionPortal>
      ) : null}
      {!immersiveMode && !filtersOpened ? <h2 className="visually-hidden">Report filters</h2> : null}
      {state.notices.map((notice) => (
        <Alert color="orange" key={notice} title="Saved or shared view adjusted">
          {notice}
        </Alert>
      ))}

      {state.validationMessage ? (
        <Alert color="orange" role="alert" title="Filters were not changed">
          {state.validationMessage}
        </Alert>
      ) : null}

      {state.loadWarnings.length > 0 ? (
        <Alert color="orange" role="alert" title="Some filter options are unavailable">
          <Stack gap="xs">
            {state.loadWarnings.map((warning) => <Text key={warning} size="sm">{warning}</Text>)}
            <Button onClick={state.retryLoads} size="compact-sm" variant="light">
              Retry loading filter options
            </Button>
          </Stack>
        </Alert>
      ) : null}

      {state.status === "blocked" ? (
        <Alert color="red" role="alert" title="This report cannot run">
          <Stack gap="xs">
            <Text size="sm">{state.message}</Text>
            <Button
              onClick={state.reason === "load-error" ? state.retryLoads : state.resetFilters}
              size="compact-sm"
              variant="light"
            >
              {state.reason === "load-error"
                ? "Retry loading filters"
                : "Reset to permitted brands"}
            </Button>
          </Stack>
        </Alert>
      ) : null}

      {filters && state.catalog ? (
        <AdNameSearchProvider filters={filters} onChange={state.setFilters}>
          {!immersiveMode ? (
            <Group className="filter-workspace__toolbar" justify="space-between">
              <Button
                aria-controls={drawerId}
                aria-expanded={filtersOpened}
                aria-label={filtersOpened ? "Close filters" : "Open filters"}
                className="filter-workspace__drawer-trigger"
                onClick={(event) => {
                  if (filtersOpened) closeFilters();
                  else openFilters("brands", event.currentTarget);
                }}
                ref={filterTriggerRef}
                variant="default"
              >
                {filtersOpened && desktopRail ? "Hide filters" : "Filters"}
              </Button>
              <Button onClick={state.resetFilters} size="compact-sm" variant="subtle">
                Reset filters
              </Button>
            </Group>
          ) : null}
          <div className={!immersiveMode && filtersOpened && desktopRail
            ? "filter-workspace__body filter-workspace__body--rail-open"
            : "filter-workspace__body"}
          >
            {!immersiveMode && desktopRail && filtersOpened ? (
              <aside aria-label="Report filters" className="filter-workspace__rail" id={drawerId}>
                {filterEditor}
              </aside>
            ) : null}
            <div className="filter-workspace__results">
              {!immersiveMode ? (
                <ActiveFilterChips
                  catalog={state.catalog}
                  controls={drawerId}
                  filters={filters}
                  onEdit={openFilters}
                  opened={filtersOpened}
                />
              ) : null}
              {state.status === "runnable" ? (
                <ReportWorkspace
                  filters={state.filters}
                  onAccessChanged={onAccessChanged}
                  onFiltersChange={state.setFilters}
                  onSessionExpired={onSessionExpired}
                  onUnknownDurationCount={setReportUnknownDurationCount}
                />
              ) : null}
            </div>
          </div>
          {!immersiveMode && !desktopRail ? (
            <Drawer
              closeButtonProps={{ "aria-label": "Close filters" }}
              id={drawerId}
              onClose={closeFilters}
              onEnterTransitionEnd={focusRequestedSection}
              onExitTransitionEnd={() => restoreFocus(
                returnFocusRef.current,
                filterTriggerRef.current,
              )}
              opened={filtersOpened}
              returnFocus={false}
              size="min(28rem, 94vw)"
              title="Filters"
              trapFocus
            >
              {filterEditor}
            </Drawer>
          ) : null}
        </AdNameSearchProvider>
      ) : null}
    </Stack>
  );
}
